import Link from "next/link";
import Image from "next/future/image";
import { getProductLink, downloadLink, productRouting } from "@/lib/utils.js";
import { CartContext } from "@/lib/cart_context.js";
import { useContext, useState } from "react";
import { targetIcon } from "@/lib/icons";
import ShareDownloadsModal from "@/components/share_downloads_modal.js";

function ProductLink({ product, children, ...props }) {
  if (product.child_brand_link) {
    return (
      <a href={getProductLink(product)} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  }

  return (
    <Link {...productRouting(product)} {...props}>
      {children}
    </Link>
  );
}

export default function ProductTile({
  attributesApplied,
  product,
  addCompareProduct,
  prodRemove,
  canCompare,
  isCompared,
  compareProducts,
  hotSpotDisplay
}) {
  const productLink = getProductLink(product);
  const cart = useContext(CartContext);

  const handleChange = (event) => {
    if (event.target.checked) {
      addCompareProduct();
    } else {
      prodRemove();
    }
  };

  const [showDownload, setShowDownloads] = useState(false);
  const contain = { objectFit: "contain" };
  const itemInCart = cart?.itemInCart && cart.itemInCart(product.id);
  const hasAttributes = product.attributes?.length > 0;

  return (
    <>
      <div
        id={product.slug}
        className={`lexcms-product-tile ${attributesApplied ? "lexcms-product-tile-with-attributes" : ""} ${product.tile_description ? "lexcms-product-tile-with-description" : ""} ${product.manufacturer_id ? "brand-" + product.manufacturer_id : ""}`}
      >
        {product.is_featured && (
          <div className="lexcms-product-featured-banner">Featured</div>
        )}

        <div
          className={`lexcms-product-tile-image-area ${attributesApplied ? "lexcms-product-tile-with-attributes-image-area" : ""} ${product.secondary_image ? "--lexcms-has-secondary-image" : ""} ${product.tile_description ? "lexcms-product-tile-with-description-image-area" : ""}`}
        >
          <ProductLink product={product}>
            {product.child_brand_link ? ( 
            <span style={{ position: "relative", height: "100%", display: "block" }}>
              <Image
                unoptimized
                src={product.image}
                alt={product.name + " product page"}
                className="lexcms-product-tile-image"
                fill
                style={contain}
              />

              {product.secondary_image && (
                <Image
                  unoptimized
                  src={product.secondary_image.url}
                  alt=""
                  fill
                  className="lexcms-product-tile-swap-image"
                  style={contain}
                />
              )}

              {hotSpotDisplay && (
                <div className="marker-container">
                  <div className="marker">{targetIcon}</div>
                </div>
              )}
            </span>
            ) :  (

            <a style={{ position: "relative", height: "100%", display: "block" }}>
              <Image
                unoptimized
                src={product.image}
                alt={product.name + " product page"}
                className="lexcms-product-tile-image"
                fill
                style={contain}
              />

              {product.secondary_image && (
                <Image
                  unoptimized
                  src={product.secondary_image.url}
                  alt=""
                  fill
                  className="lexcms-product-tile-swap-image"
                  style={contain}
                />
              )}

              {hotSpotDisplay && (
                <div className="marker-container">
                  <div className="marker">{targetIcon}</div>
                </div>
              )}
            </a> )
            }
          </ProductLink>

          {((canCompare && compareProducts.length < 4) ||
            (isCompared && canCompare)) && (
            <label className="lexcms-product-tile-compare-checkbox-container">
              <input
                type="checkbox"
                onChange={handleChange}
                checked={isCompared}
                className="lexcms-product-tile-compare-checkbox"
              />
              <span className="lexcms-product-tile-compare-checkbox-label">
                Compare
              </span>
            </label>
          )}

          {cart?.itemInCart && (
            <label className="lexcms-product-tile-compare-checkbox-container lexcms-tile-cart-add">
              <input
                type="checkbox"
                onChange={() =>
                  itemInCart
                    ? cart.removeItem(product.id)
                    : cart.addItem(product.id)
                }
                checked={!!itemInCart}
                className="lexcms-product-tile-compare-checkbox"
              />
              <span className="lexcms-product-tile-compare-checkbox-label">
                {itemInCart ? "Remove" : "Save"}
              </span>
            </label>
          )}
        </div>

        <div className="lexcms-product-tile-text-area">
          {product.is_new && (
            <ProductLink product={product}>
              <div className="lexcms-product-tile-new-product">New Product</div>
            </ProductLink>
          )}

          <ProductLink product={product}>
            <span>
              <p className="lexcms-product-tile-name">{product.name}</p>
            </span>
          </ProductLink>

          <div className="lexcms-product-tile-subname-container">
            {product.sub_name && (
              <ProductLink product={product}>
                <p className="lexcms-product-tile-subname">{product.sub_name}</p>
              </ProductLink>
            )}
          </div>

          <div className="lexcms-product-tile-model-container">
            {product.model_code && (
              <ProductLink product={product}>
                <p className="lexcms-product-tile-model-code">
                  <span>Model#: </span>
                  {product.model_code}
                </p>
              </ProductLink>
            )}
          </div>

          {product.tile_description && (
            <div className="lexcms-product-tile-description-container">
              <ProductLink product={product}>
                <div
                  className="lexcms-product-tile-description"
                  dangerouslySetInnerHTML={{
                    __html: product.tile_description
                  }}
                />
              </ProductLink>
            </div>
          )}

          {product.manufacturer_name && (
            <p className="lexcms-product-tile-manufacturer-name">
              {product.manufacturer_name}
            </p>
          )}

          {hasAttributes && (
            <div className="lexcms-product-tile-attribute-container">
              {product.attributes.map((attr, i) => (
                <p key={i} className="lexcms-product-tile-attribute-value">
                  <strong>{attr.name}: </strong>
                  {attr.values}
                </p>
              ))}
            </div>
          )}

          {product?.certifications?.length > 0 && (
            <div className="lexcms-product-tile-certs">
              {product.certifications.map((cert, i) => (
                <div key={i} className="lexcms-product-cert-image-container">
                  <Image
                    priority
                    src={cert.url}
                    height={65}
                    width={65}
                    alt=""
                    className="lexcms-product-cert-image"
                  />
                </div>
              ))}
            </div>
          )}

          {product.primary_spec_sheet?.url && (
            <a
              href={downloadLink(product.primary_spec_sheet)}
              target="_blank"
              rel="noopener noreferrer"
              className="lexcms-regular-button lexcms-product-tile-spec-sheet-button"
            >
              Specification Sheet
            </a>
          )}

          {product.download_modal_on_product_tiles && (
            <a
              onClick={() => setShowDownloads(true)}
              className="lexcms-regular-button lexcms-product-tile-spec-sheet-button"
            >
              Downloads
            </a>
          )}

          <ProductLink product={product} >
            <div className="lexcms-product-tile-details-button">Product Details</div>
          </ProductLink>
        </div>
      </div>

      {showDownload && (
        <ShareDownloadsModal
          product={product}
          loadsDownloadsAsync={true}
          setShowShare={setShowDownloads}
          showShare={showDownload}
          type="download"
        />
      )}
    </>
  );
}
